import type { QueryClient } from '@tanstack/react-query';

export type ClearReactQueryCachesAfterSessionContextChangeOptions = {
  /** Web connected-account path resets the client before clearing non-company caches */
  resetQueriesForConnectedUserPath?: boolean;
};

/** Query roots that are session-scoped (company, personal, or target user). */
export const SESSION_SCOPED_QUERY_ROOTS = new Set([
  'projects-paginated',
  'projects-infinite',
  'projectData-fallback',
  'projectFiles',
  'data-folders',
  'data-folders-infinite',
  'dataFolders',
  'dataFoldersPaginated',
  'dataFolders-fallback',
  'dataFolders-templates',
  'data-content',
  'dataContent',
  'individualData',
  'individualData-fallback',
  'favouriteData',
  'mediaLibrary',
  'knowledge',
  'knowledge-no-profile',
  'knowledge-fallback',
  'internal-knowledge',
  'all-users',
  'userList',
  'userHierarchy',
  'companyMembers',
  'statCount',
  'userStats',
  'userStatsTrends',
  'calendars',
  'calendarData',
  'calendar-breakdown',
  'sent-invoices',
  'subscriptionPlan',
  'billingInfo',
  'planData',
  'planSubscriptions',
  'integrationData',
]);

const keepCompanyList = (query: { queryKey: readonly unknown[]; getObserversCount: () => number }) =>
  query.queryKey[0] !== 'companyList';

export const isSessionScopedQueryKey = (queryKey: unknown) => {
  const root = Array.isArray(queryKey) ? queryKey[0] : queryKey;
  return typeof root === 'string' && SESSION_SCOPED_QUERY_ROOTS.has(root);
};

/**
 * After `switchCompany` changes session (company, personal, or target user),
 * reset session-scoped React Query caches so screens like Projects refetch.
 * Keeps `companyList` so the account picker can still render during navigation.
 *
 * `resetQueries` clears cached data and refetches any still-mounted observers,
 * which `removeQueries` alone does not reliably do for active project lists.
 */
export function clearReactQueryCachesAfterSessionContextChange(
  queryClient: QueryClient,
  options?: ClearReactQueryCachesAfterSessionContextChangeOptions,
): void {
  void queryClient.resetQueries({
    predicate: keepCompanyList,
  });

  if (options?.resetQueriesForConnectedUserPath) {
    return;
  }

  queryClient.removeQueries({
    predicate: (query) => keepCompanyList(query) && query.getObserversCount() === 0,
  });
}
